import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import '@ygtechnologies/bygr-frontend-component-library/dist/index.css';
import '@/css/normalize.css';
import '@/css/base.scss';
import 'react-datepicker/dist/react-datepicker.css';
import '@/css/forms.scss';
import '@/css/react-datepicker.scss';
import '@/css/main.scss';
import '@ygtechnologies/bygr-frontend-component-library/dist/index.css';
import '@ygtechnologies/bygr-frontend-chat/dist/index.css';
import '@ygtechnologies/rubus-ui/dist/index.css';

import { BrowserRouter as Router } from 'react-router-dom';
import { SentryErrorHandler as SentryToolsErrorHandler } from '@ygtechnologies/bygr-frontend-tools';

import AjaxErrorHandler from './AjaxErrorHandler';

import {
    ComponentLibraryLocalizationProvider,
    initializeIconLibrary,
    LoadingIndicator,
    StickyContainer,
    ToastContainer,
} from '@ygtechnologies/bygr-frontend-component-library';

import { loadUser, setCurrentCulture, loadConfiguration, setActiveProjectId } from '@/state/global/creators';
import { DefaultCulture, SupportedCultures, tryGetCultureInfo } from '@/utils/localization';
import MainRouting from './MainRouting';
import TermsModalWrapper from '@/components/TermsModal';
import WindowSizeListener from './WindowListener';
import NotFound from '@/components/error-pages/NotFound';
import { useAppDispatch, useAppSelector, useCurrentCulture } from '@/utils/hooks';
import { ProfileType } from '@/types';
import { loadBuyerDocumentTypes } from '@/state/buyer/document-types/creator';
import { loadSellerDocumentTypes } from '@/state/seller/document-types/creator';
import ClientStorage from '@/ClientStorage';
import { logVisit } from '@/services/users';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import ScrollToTop from './ScrollToTop';
import Header from '@/components/layout/Header/Header';
import Content from '@/components/layout/Content';
import NotificationListener from '@/components/notifications/NotificationListener';
import { Contexts } from './Contexts';
import { HubspotChat } from './HubspotChat';

export const SentryDsn = 'https://eb38476080864542a976743fa4965b11@sentry.bygr.no/3';

// The public showroom is anonymous and must not trigger profile/project loaders even when
// Redux still holds a logged-in profile from a previous session in the same browser.
const isShowroomRoute = () => {
    const base = (import.meta.env.BASE_URL as string) || '/';
    return window.location.pathname.startsWith(`${base}showroom/`);
};

type AppProps = {};

const App: React.FC<AppProps> = () => {
    const dispatch = useAppDispatch();
    const [cultureInfo] = useCurrentCulture();

    const { user, configuration, activeProfile, activeProject } = useAppSelector((state) => state.global);
    useEffect(() => {
        loadUser(dispatch);
    }, [dispatch]);

    useEffect(() => {
        if (!configuration) {
            loadConfiguration(dispatch);
        }
    }, [configuration, dispatch]);

    useEffect(() => {
        if (user) {
            setCurrentCulture(dispatch, tryGetCultureInfo(user.locale) || DefaultCulture);
        }
    }, [user, dispatch]);

    useEffect(() => {
        if (user && user.isLoggedIn) {
            logVisit();
        }
    }, [user]);

    useEffect(() => {
        if (isShowroomRoute()) return;
        if (!activeProfile) {
            return;
        }
        if (activeProfile.type === ProfileType.Buyer) {
            loadBuyerDocumentTypes(dispatch);
        } else if (activeProfile.type === ProfileType.Seller && activeProject) {
            loadSellerDocumentTypes(dispatch, activeProject.id);
        }
    }, [cultureInfo, activeProfile, activeProject, dispatch]);

    useEffect(() => {
        if (isShowroomRoute()) return;
        async function load() {
            const lastProjectId = ClientStorage.lastProjectId;
            if (!activeProject && activeProfile && activeProfile.type === ProfileType.Seller) {
                try {
                    await setActiveProjectId(dispatch, lastProjectId, true);
                } catch {
                    ClientStorage.lastProjectId = null;
                }
            }
        }

        load();

        if (activeProject) {
            ClientStorage.lastProjectId = activeProject.id;
        }
    }, [activeProfile, activeProject, dispatch]);
    useEffect(() => {
        initializeIconLibrary();
    }, []);
    // useEffect(() => {
    //     const handleResize = () => {
    //         setDimensions({
    //             width: window.innerWidth,
    //             height: window.innerHeight,
    //         });
    //     };
    //     window.addEventListener('resize', handleResize);
    //     return () => {
    //         window.removeEventListener('resize', handleResize);
    //     };
    // }, []);
    if (!user || !cultureInfo || !configuration) {
        return <LoadingIndicator />;
    }

    if (!configuration.valid) {
        return <NotFound />;
    }

    const appTree = (
        <Contexts>
            <Router
                getUserConfirmation={() => {
                    /* Empty callback to block the default browser prompt
                            user by RouterPrompt.
                        */
                }}
                basename={import.meta.env.BASE_URL}>
                <StickyContainer>
                    <Header />
                    {/* <OldHeader /> */}

                    <ToastContainer />
                    <AjaxErrorHandler />

                    <NotificationListener />
                    <WindowSizeListener />
                    <TermsModalWrapper />
                    <ScrollToTop />
                    {/* {!user.isLoggedIn && <HubspotChat />} */}

                    <Content>
                        <MainRouting isLoggedIn={user.isLoggedIn} />
                    </Content>
                </StickyContainer>
            </Router>
        </Contexts>
    );

    return (
        <ComponentLibraryLocalizationProvider
            onLanguageChange={(lc) => console.log('Language changed to -- Think this can be removed', lc)}
            supportedLanguageCodes={SupportedCultures.map((x) => x.locale)}
            languageCode={cultureInfo.locale}>
            {/* The public showroom is anonymous and often embedded in a third-party iframe, where
                envelopes to sentry.bygr.no hang (no reachability / blocked) and there's no logged-in
                user to attribute errors to. Render it outside the Sentry boundary so the SDK never
                initializes for that route. */}
            {isShowroomRoute() ? (
                appTree
            ) : (
                <SentryToolsErrorHandler
                    user={{ email: user?.email || '', id: user?.userId.toString() || '', name: user?.displayName }}
                    application="CM"
                    environment={window.APP_CONFIG.ENVIRONMENT}
                    dsn={SentryDsn}
                    popupTitle={t`Something went wrong`}
                    popupMessage={
                        <Trans>
                            Dear user,
                            <br />
                            We are sorry, but something went wrong. We have been notified and will look into it.
                        </Trans>
                    }
                    popupButtonCloseLabel={t`Close`}
                    popupButtonOpenFeedbackLabel={t`Report problem`}>
                    {appTree}
                </SentryToolsErrorHandler>
            )}
        </ComponentLibraryLocalizationProvider>
    );
};

export default App;
